import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { TYPING_EXPIRY_MS } from "../../../shared/employee/messages";

/**
 * Typing and presence: the two signals that are too fast to be cache.
 *
 * Both arrive over the socket many times a minute. Treating them as RTK Query
 * invalidation would refetch a thread every few seconds because somebody is
 * typing in it — a self-inflicted load problem in exchange for a three-dot
 * animation. They live here instead: in memory, never persisted, and gone on
 * reload, which is exactly the lifetime they should have.
 *
 * Typing entries carry the time they arrived rather than a timer, because a
 * timer per conversation is a leak waiting to happen and a dropped socket would
 * leave someone "typing…" forever. Readers decide what is stale.
 */

interface PresenceState {
  /** conversationId → partyId → when their last typing event arrived. */
  typing: Record<string, Record<string, number>>;
  /** partyId → online. Absent means unknown, which renders as offline. */
  online: Record<string, boolean>;
  /** partyId → ISO timestamp, for "last seen". */
  lastSeen: Record<string, string>;
}

const initialState: PresenceState = { typing: {}, online: {}, lastSeen: {} };

export const employeePresenceSlice = createSlice({
  name: "employeePresence",
  initialState,
  reducers: {
    typingReceived(
      state,
      action: PayloadAction<{ conversationId: string; partyId: string; at: number }>,
    ) {
      const { conversationId, partyId, at } = action.payload;
      state.typing[conversationId] = { ...(state.typing[conversationId] ?? {}), [partyId]: at };
    },
    presenceReceived(
      state,
      action: PayloadAction<{ partyId: string; online: boolean; lastSeenAt?: string | null }>,
    ) {
      const { partyId, online, lastSeenAt } = action.payload;
      state.online[partyId] = online;
      if (lastSeenAt) state.lastSeen[partyId] = lastSeenAt;
    },
    /**
     * Someone stopped typing — because they sent the message.
     *
     * Waiting for the entry to age out leaves the bubble sitting under a
     * message that has already arrived, which reads as the app being behind.
     * The message IS the proof they finished.
     */
    typingCleared(state, action: PayloadAction<{ conversationId: string; partyId: string }>) {
      const { conversationId, partyId } = action.payload;
      const people = state.typing[conversationId];
      if (!people?.[partyId]) return;
      delete people[partyId];
      if (Object.keys(people).length === 0) delete state.typing[conversationId];
    },

    /** Drop entries older than the expiry, so a dropped socket can't pin one on. */
    typingSwept(state, action: PayloadAction<{ now: number }>) {
      for (const [conversationId, people] of Object.entries(state.typing)) {
        const stale = Object.entries(people).filter(
          ([, at]) => action.payload.now - at >= TYPING_EXPIRY_MS,
        );
        // Only touch state when something actually expired. Reassigning every
        // tick would re-render every open thread once a second for nothing.
        if (stale.length === 0) continue;
        for (const [partyId] of stale) delete people[partyId];
        if (Object.keys(people).length === 0) delete state.typing[conversationId];
      }
    },
  },
});

export const { typingReceived, typingCleared, presenceReceived, typingSwept } =
  employeePresenceSlice.actions;
export const employeePresenceReducer = employeePresenceSlice.reducer;

/** Who is typing in this conversation right now, excluding stale entries. */
export function selectTyping(
  state: { employeePresenceReducer: PresenceState },
  conversationId: string,
  now = Date.now(),
): string[] {
  const people = state.employeePresenceReducer.typing[conversationId] ?? {};
  return Object.entries(people)
    .filter(([, at]) => now - at < TYPING_EXPIRY_MS)
    .map(([partyId]) => partyId);
}

export function selectPresence(
  state: { employeePresenceReducer: PresenceState },
  partyId: string,
): { online: boolean; lastSeenAt: string | null } {
  return {
    online: state.employeePresenceReducer.online[partyId] === true,
    lastSeenAt: state.employeePresenceReducer.lastSeen[partyId] ?? null,
  };
}
